import React from 'react';
import _ from 'lodash';
import NoDataChart from './NoDataChart';
import moment from 'moment';
import LocationChartTooltip from './LocationChartTooltip';
import { renderToString } from 'react-dom/server';
import { ONE_WEEK, ONE_DAY, ONE_MONTH } from '../../../const/periodRanges';
import { importExternalScript } from '../../../services/ExternalScriptImporter';


const GOOGLE_MAPS_SCRIPT_LOCATION = process.env.GOOGLE_MAPS_SCRIPT_LOCATION;
const radiusIncreaser = 40;
const defaultCenter = { lat: 48, lng: -10 };
const defaultZoom = 3;
const minZoom = .5;
const maxZoom = 22;
const zoomScaleStep = 5;
const zoomStep = 1;
const googleMapOptions = {
    mapTypeId: 'roadmap',
    streetViewControl: false,
    zoomControl: false,
    disableDefaultUI: true,
    zoom: defaultZoom,
    restriction: {
        latLngBounds: {
            north: 85,
            south: -85,
            west: -180,
            east: 180
        }
    },
};


function drawCircle(point, radius, dir, map) {
    var d2r = Math.PI / 180;   // degrees to radians
    var r2d = 180 / Math.PI;   // radians to degrees
    var earthsradius = 3963; // 3963 is the radius of the earth in miles
    var points = 32;
    // find the raidus in lat/lon
    var rlat = (radius / earthsradius) * r2d;
    var rlng = rlat / Math.cos(point.lat() * d2r);
    var start, end, ex, ey;

    var extp = [];
    if (dir === 1) { start = 0; end = points + 1; } // one extra here makes sure we connect the
    else { start = points + 1; end = 0; }
    for (var i = start; (dir === 1 ? i < end : i > end); i = i + dir) {
        var theta = Math.PI * (i / (points / 2));
        ey = point.lng() + (rlng * Math.cos(theta)); // center a + radius x * cos(theta)
        ex = point.lat() + (rlat * Math.sin(theta)); // center b + radius y * sin(theta)
        extp.push(new google.maps.LatLng(ex, ey));
    }
    return extp;
}

function getCssForTooltip(e, point) {
    var event = e.tb;
    var deltaX = 20, deltaY = 0;
    var mapWidth, mapHeight;
    if (event) {
        mapWidth = event.srcElement.clientWidth;
        mapHeight = event.srcElement.clientHeight;
    }

    if (point.x > 0.6 * mapWidth) {
        deltaX = -150;
    }

    if (point.y > 0.6 * mapHeight) {
        deltaY = -40;
    }

    return {
        'transform': `translate(${point.x + deltaX}px, ${point.y + deltaY}px)`
    };
}

class LocationChart extends React.Component {
    constructor(props, context) {
        super(props, context);
        this._isMounted = false;
        this.donuts = [];
        this.googleMap = null;
        this.googleMapOverlay = null;
        this.googleMapsLoaded = importExternalScript(GOOGLE_MAPS_SCRIPT_LOCATION);
    }

    componentDidMount() {
        this._isMounted = true;
        const { chartData } = this.props;
        this.updateMap(chartData, true);
    }

    componentDidUpdate(prevProps) {
        const { chartData } = this.props;

        if (chartData && !this.googleMap) {
            setTimeout(() => {
                this.updateMap(chartData, true);
            });
        }
        if (this.googleMap) {
            if (prevProps.chartData !== chartData) {
                this.updateMap(chartData, true);
            }
        }
    }

    componentWillUnmount() {
        this.googleMap = null;
        this.googleMapOverlay = null;
        this.removeDonuts(this.donuts);
        this._isMounted = false;
    }

    updateMap(chartData, forceUpdate) {
        if (!this._isMounted) return;

        const circles = this.getCirclesForMap(chartData);
        const defCenter = circles.length > 0 ? circles[0].center : defaultCenter;
        var mapEl = this.refs.map;
        if (!mapEl) return;

        if (!this.googleMap || forceUpdate) {
            this.googleMapsLoaded.then(() => {
                this.googleMap = new google.maps.Map(mapEl, {
                    ...googleMapOptions,
                    center: defCenter,
                });
                google.maps.event.addListener(this.googleMap, 'zoom_changed', () => {
                    if (this.googleMap) {
                        var zoomChangeBoundsListener =
                            google.maps.event.addListener(this.googleMap, 'bounds_changed', () => {
                                if (this.googleMap) {
                                    if (this.googleMap.getZoom() >= zoomScaleStep && this.googleMap.initialZoom === true) {
                                        this.googleMap.setZoom(defaultZoom);
                                        this.googleMap.initialZoom = false;
                                    }
                                }
                                google.maps.event.removeListener(zoomChangeBoundsListener);
                            });
                    }
                });
                this.googleMapOverlay = new google.maps.OverlayView();
                this.googleMapOverlay.draw = function () { };
                this.googleMapOverlay.setMap(this.googleMap);
                this.updateCirclesOnMap(circles);
            });
        } else {
            this.updateCirclesOnMap(circles);
        }
    }

    updateCirclesOnMap(circles) {
        this.removeDonuts();

        var latlngbounds = new google.maps.LatLngBounds();

        for (let i = 0; i < circles.length; i++) {
            let circle = circles[i];
            let paths;
            latlngbounds.extend(new google.maps.LatLng(circle.center.lat, circle.center.lng));

            if (circle.type === 'connections') {
                paths = [
                    drawCircle(new google.maps.LatLng(circle.center.lat, circle.center.lng), circle.radius, 1, this.googleMap),
                ];
            }
            else {
                paths = [
                    drawCircle(new google.maps.LatLng(circle.center.lat, circle.center.lng), circle.radius, 1, this.googleMap)
                ];
            }

            let donut = new google.maps.Polygon({
                paths: paths,
                strokeWeight: 0,
                tooltipInfo: circle.tooltipInfo,
                googleMapOverlay: this.googleMapOverlay,
                ...(circle.type === 'connections' ?
                    {
                        strokeColor: circle.color,
                        strokeOpacity: 0.6,
                        strokeWeight: 2,
                        fillColor: 'transparent',
                    } :
                    {
                        fillColor: circle.color,
                        fillOpacity: 0.6,
                    })
            });

            donut.setMap(this.googleMap);
            donut.addListener('mouseover', this.showTooltip);
            donut.addListener('mousemove', this.moveTooltip);
            donut.addListener('mouseout', this.hideTooltip);

            this.donuts.push(donut);
        }

        if (circles.length > 0) {
            this.googleMap.initialZoom = true;
            this.googleMap.setCenter(latlngbounds.getCenter());
            this.googleMap.fitBounds(latlngbounds);
        } else {
            this.googleMap.setZoom(minZoom);
        }
    }

    removeDonuts() {
        for (let i = 0; i < this.donuts.length; i++) {
            let donut = this.donuts[i];
            donut.setMap(null);
            google.maps.event.clearListeners(donut, 'mouseover');
            google.maps.event.clearListeners(donut, 'mousemove');
            google.maps.event.clearListeners(donut, 'mouseout');
        }
        this.donuts = [];
    }

    showTooltip(e) {
        var donut = this;
        var projection = this.googleMapOverlay.getProjection();
        var point = projection.fromLatLngToContainerPixel(e.latLng);
        var $tooltip = $('.location-chart-tooltip-holder', $(e.domEvent.currentTarget).closest('.location-chart_body'));
        $tooltip.html(renderToString(<LocationChartTooltip {...donut.tooltipInfo} />));
        $tooltip.css(getCssForTooltip(e, point)).show();
    }

    moveTooltip(e) {
        var projection = this.googleMapOverlay.getProjection();
        var point = projection.fromLatLngToContainerPixel(e.latLng);
        var $tooltip = $('.location-chart-tooltip-holder', $(e.domEvent.currentTarget).closest('.location-chart_body'));
        $tooltip.css(getCssForTooltip(e, point));
    }

    hideTooltip(e) {
        $('.location-chart-tooltip-holder', $(e.domEvent.currentTarget).closest('.location-chart_body')).hide();
    }

    getCirclesForMap(chartData) {
        let circles = [];
        let accountUsages = [];
        if (chartData) {
            chartData.forEach(point => {
                let center = { lat: point.latitude, lng: point.longitude };
                accountUsages.push(
                    {
                        color: '#FF4A4A',
                        type: 'interactions',
                        center: center,
                        radius: Math.log(+point.interactions + 1) * radiusIncreaser,
                        tooltipInfo: point
                    });
                accountUsages.push(
                    {
                        color: '#2200ff',
                        center: center,
                        type: 'connections',
                        internalRadius: Math.log(+point.interactions + 1) * radiusIncreaser,
                        radius: Math.log(+point.connections + 1) * radiusIncreaser,
                        tooltipInfo: point
                    });
            });
            circles = _.sortBy(accountUsages, accountUsage => -accountUsage.radius);
        }
        return circles;
    }

    zoomOutMap = () => {
        if (this.googleMap) {
            const newZoom = this.googleMap.getZoom() + zoomStep;

            if (newZoom >= 0) {
                this.googleMap.setZoom(newZoom);
            }
            this.manageZoomIcons(newZoom);
        }
    }

    zoomInMap = () => {
        if (this.googleMap) {
            const newZoom = this.googleMap.getZoom() - zoomStep;
            if (newZoom >= 0) {
                this.googleMap.setZoom(newZoom);
                this.manageZoomIcons(newZoom);
            }
        }
    }

    manageZoomIcons(zoom) {
        const zoomInIcon = this.refs.zoomInIcon;
        const zoomOutIcon = this.refs.zoomOutIcon;

        if (zoom <= minZoom) {
            zoomInIcon.classList.add('disabled');
        } else {
            zoomInIcon.classList.remove('disabled');
        }
        if (zoom >= maxZoom) {
            zoomOutIcon.classList.add('disabled');
        } else {
            zoomOutIcon.classList.remove('disabled');
        }
    }

    centerMap = () => {
        const { chartData } = this.props;
        const circles = this.getCirclesForMap(chartData);
        const defCenter = circles.length > 0 ? circles[0].center : defaultCenter;
        const center = new google.maps.LatLng(defCenter.lat, defCenter.lng);
        if (this.googleMap) {
            this.googleMap.panTo(center);
            this.googleMap.setZoom(defaultZoom);
            this.manageZoomIcons(defaultZoom);
        }
    }

    render() {
        const {
            chartData,
            emptyRows
        } = this.props;

        // console.info('render', chartData);

        return (
            <div className="location-chart">
                {
                    _.isEmpty(chartData) === false ?
                        <div className="location-chart_internal-wrapper">
                            <div className="location-chart_body">
                                <div key="map" ref="map"></div>
                                <div className="location-chart-tooltip-holder"></div>
                                <div className="location-chart-controls">
                                    <i className="location-chart-controls_icon icon icon-alan-zoom-in" ref="zoomInIcon" onClick={this.zoomInMap}></i>
                                    <i className="location-chart-controls_icon icon icon-alan-zoom-out" ref="zoomOutIcon" onClick={this.zoomOutMap}></i>
                                    <i className="location-chart-controls_icon icon icon-center-map" onClick={this.centerMap}></i>
                                </div>
                            </div>
                        </div> :
                        <NoDataChart data={chartData} emptyRows={emptyRows} />
                }
            </div>

        );
    }
}

export default LocationChart;
